import { getDataSource } from './dataSource';
import { Appointment, CreateAppointmentData } from '@/types/appointment';
import { Patient } from '@/types/patient';

export interface AppointmentFilters {
  page?: number;
  limit?: number;
  status?: string;
  providerId?: string;
  patientId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface AppointmentListResponse {
  appointments: Appointment[];
  total: number;
}

export interface AppointmentWithPatient {
  id: string;
  patientId: Patient;
  appointmentDetails: {
    scheduledDate: string;
    type: string;
    location: string;
    chiefComplaint?: string;
  };
  recording: {
    status: string;
  };
}

class AppointmentWrapperService {
  async getAppointments(filters: AppointmentFilters = {}): Promise<AppointmentListResponse> {
    // Filter out unsupported parameters for the dataSource
    const dsFilters = {
      page: filters.page,
      limit: filters.limit,
      status: filters.status,
      providerId: filters.providerId
    };
    
    try {
      const response = await getDataSource().getAppointments(dsFilters);
      // API response received
      
      const appointments = response.appointments || [];
      
      // If date filters are provided, filter the results client-side
      let filteredAppointments = appointments;
      
      if (filters.dateFrom || filters.dateTo) {
        filteredAppointments = appointments.filter(apt => {
          const scheduledDate = new Date(apt.appointmentDetails.scheduledDate);
          
          if (filters.dateFrom && scheduledDate < new Date(filters.dateFrom)) {
            return false;
          }
          
          if (filters.dateTo && scheduledDate > new Date(filters.dateTo)) {
            return false;
          }
          
          return true;
        });
      }
      
      if (filters.patientId) {
        filteredAppointments = filteredAppointments.filter(apt => 
          apt.patientId === filters.patientId
        );
      }
      
      return {
        appointments: filteredAppointments || [],
        total: (filteredAppointments || []).length
      };
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      throw error;
    }
  }

  async getAppointment(appointmentId: string): Promise<Appointment> {
    try {
      return await getDataSource().getAppointment(appointmentId);
    } catch (error) {
      console.error('Failed to fetch appointment:', error);
      throw error;
    }
  }

  async createAppointment(data: CreateAppointmentData): Promise<Appointment> {
    try {
      // Convert CreateAppointmentData to full Appointment object
      const fullAppointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'> = {
        organizationId: '', // Will be set by backend
        patientId: data.patientId,
        providerId: data.providerId || '',
        recordedBy: data.providerId || '',
        appointmentDetails: data.appointmentDetails,
        recording: {
          status: 'pending',
          startTime: undefined,
          endTime: undefined,
          fileSize: undefined,
          audioQuality: undefined,
          deviceInfo: undefined
        },
        transcription: {
          status: 'pending',
          model: 'base.en',
          confidence: undefined,
          wordCount: undefined,
          processingTime: undefined,
          completedAt: undefined,
          errorMessage: undefined
        },
        clinicalNote: {
          status: 'pending',
          generatedAt: undefined,
          reviewedAt: undefined,
          approvedAt: undefined,
          version: 1
        },
        isDeleted: false
      };
      
      return await getDataSource().createAppointment(fullAppointmentData);
    } catch (error) {
      console.error('Failed to create appointment:', error);
      throw error;
    }
  }

  async getPatient(patientId: string): Promise<Patient> {
    try {
      return await getDataSource().getPatient(patientId);
    } catch (error) {
      console.error('Failed to fetch patient:', error);
      throw error;
    }
  }
}

export const appointmentWrapperService = new AppointmentWrapperService();